import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  etiquetaNivelGanancia,
  formatoPct,
  GANANCIA_SUGERIDA_PCT,
  GananciaVesClp,
  gananciaVesClp,
  posicionEnRegla,
} from './margen-ves-clp';

export interface OperadorData {
  operador: 'MANUAL' | 'BINANCE' | 'CBPAY';
  descuentoFiat: number;
  comision: number;
  comisionVesClp: number;
  rateVesUsdt: number;
  usdClp: number;
}

@Component({
  selector: 'app-edit-operator-modal',
  templateUrl: './edit-operator-modal.component.html',
  styleUrls: ['./edit-operator-modal.component.sass']
})
export class EditOperatorModalComponent implements OnInit {
  form: FormGroup;
  operadores = [
    { value: 'MANUAL', label: 'Manual' },
    { value: 'BINANCE', label: 'Binance' },
    { value: 'CBPAY', label: 'CBPAY' }
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EditOperatorModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      operadorActivo: string;
      parametros: {
        manual: OperadorData;
        binance: OperadorData;
        cbpay: OperadorData;
      };
    }
  ) {
    this.form = this.fb.group({
      operador: ['MANUAL', Validators.required],
      descuentoFiat: [0, [Validators.required, Validators.min(0.0001)]],
      comision: [0, [Validators.required, Validators.min(0.01)]],
      comisionVesClp: [0, [Validators.required, Validators.min(0)]],
      rateVesUsdt: [0, [Validators.required, Validators.min(0.01)]],
      usdClp: [0, [Validators.required, Validators.min(0.01)]]
    });
  }

  ngOnInit(): void {
    // Cargar valores del operador activo por defecto
    const operadorInicial = this.data.operadorActivo.toUpperCase() as 'MANUAL' | 'BINANCE' | 'CBPAY';
    this.form.patchValue({ operador: operadorInicial });
    this.cargarParametrosOperador(operadorInicial);

    // Escuchar cambios en el selector de operador
    this.form.get('operador')?.valueChanges.subscribe((operador) => {
      this.cargarParametrosOperador(operador);
    });
  }

  /**
   * Carga los parámetros del operador seleccionado
   */
  cargarParametrosOperador(operador: 'MANUAL' | 'BINANCE' | 'CBPAY'): void {
    let params: OperadorData;

    switch (operador) {
      case 'MANUAL':
        params = this.data.parametros.manual;
        // En MANUAL, el rate es editable
        this.form.get('rateVesUsdt')?.enable();
        break;
      case 'BINANCE':
        params = this.data.parametros.binance;
        // En BINANCE, el rate es automático
        this.form.get('rateVesUsdt')?.disable();
        break;
      case 'CBPAY':
        params = this.data.parametros.cbpay;
        // En CBPAY, el rate es automático
        this.form.get('rateVesUsdt')?.disable();
        break;
    }

    this.form.patchValue({
      descuentoFiat: params.descuentoFiat,
      comision: params.comision,
      comisionVesClp: params.comisionVesClp || 0,
      rateVesUsdt: params.rateVesUsdt,
      usdClp: params.usdClp
    });
  }

  /**
   * Verifica si el rate VES/USDT es editable
   */
  get isRateEditable(): boolean {
    return this.form.get('operador')?.value === 'MANUAL';
  }

  readonly gananciaSugeridaPct = GANANCIA_SUGERIDA_PCT;

  /** Marcas de la regla (−1 %, 0, +0,5 %, +2 %) con su posición. */
  readonly marcasRegla = [
    { texto: '−1 %', pos: posicionEnRegla(-1) },
    { texto: '0', pos: posicionEnRegla(0) },
    { texto: '+0,5 %', pos: posicionEnRegla(0.5) },
    { texto: '+2 %', pos: posicionEnRegla(2) },
  ];

  /** «2,33»: dos decimales con coma (es-CL). */
  numero(n: number): string {
    return Number(n).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  etiquetaNivel(g: GananciaVesClp): string {
    return etiquetaNivelGanancia(g.nivel);
  }

  pct(n: number): string {
    return formatoPct(n);
  }

  posicion(g: GananciaVesClp): number {
    return posicionEnRegla(g.gananciaPct);
  }

  /**
   * Ganancia real de VES→CLP con lo que hay escrito en el formulario, en vivo.
   * Solo informativa: ver `margen-ves-clp.ts`.
   */
  get gananciaVesClp(): GananciaVesClp | null {
    const v = this.form.getRawValue();
    return gananciaVesClp(v.rateVesUsdt, v.usdClp, v.comision, v.comisionVesClp);
  }

  /**
   * Guarda los cambios
   */
  onSave(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue(); // getRawValue incluye campos disabled

      // Preparar datos para enviar al backend
      const updateData = {
        operador: formValue.operador,
        descuentoFiat: formValue.descuentoFiat,
        comision: formValue.comision,
        comisionVesClp: formValue.comisionVesClp,
        usdClp: formValue.usdClp
      };

      // Solo incluir rateVesUsdt si es MANUAL (editable)
      if (formValue.operador === 'MANUAL') {
        Object.assign(updateData, { rateVesUsdt: formValue.rateVesUsdt });
      }

      this.dialogRef.close(updateData);
    }
  }

  /**
   * Cancela y cierra el modal
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Obtiene el mensaje de error para un campo
   */
  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);

    if (field?.hasError('required')) {
      return 'Este campo es requerido';
    }

    if (field?.hasError('min')) {
      const minValue = field.errors?.['min']?.min;
      if (minValue === 0) {
        return 'El valor debe ser mayor o igual a 0';
      } else if (minValue === 0.01) {
        return 'El valor debe ser mayor a 0';
      }
      return `El valor debe ser mayor o igual a ${minValue}`;
    }

    return '';
  }
}
